import React, { useEffect, useState } from 'react';
import { Field, FieldArrayRenderProps } from 'formik';
import Select from 'react-select';
import { Button } from 'react-bootstrap';
import Text from 'common/components/Text/Text';
import { CommonHash, Subject, Country, Program, City, School } from 'app/types';
import styles from '../LandingForm.module.scss';
import { CONTENT_GRID_TYPES } from 'common/constants';
import {
  useGetAllBackofficeProgramsQuery,
  useGetAllCountriesQuery,
  useGetAllSubjectsQuery,
  useGetSchoolsSearchQuery,
  useGetCitiesSearchQuery,
} from 'services/exploreApi';

interface SubComponentFormProps {
  landing_component: CommonHash;
  index: number;
  name: string;
  handleChange: {
    (e: React.ChangeEvent<any>): void;
    <T = string | React.ChangeEvent<any>>(field: T): T extends React.ChangeEvent<any>
      ? void
      : (e: string | React.ChangeEvent<any>) => void;
  };
  arrayHelpers: FieldArrayRenderProps;
  limit: number;
}

const TabForm = ({
  landing_component,
  index,
  name,
  handleChange,
  arrayHelpers,
  limit,
}: SubComponentFormProps): React.ReactElement => {
  const tabTypesOptions = CONTENT_GRID_TYPES.map(([label, value]) => ({
    label: label,
    value: value,
  }));

  const [tabType, setTabType] = useState<CommonHash>({
    label:
      tabTypesOptions.find(
        (option) =>
          option.value === landing_component.sub_landing_components[index]['config']['tab_type']
      )?.label || 'Subjects',
    value: landing_component.sub_landing_components[index]['config']['tab_type'] || 'subjects',
  });
  const [tabDisplayOptions, setTabDisplayOptions] = useState<CommonHash[]>([
    {
      label: 'Show popular ones',
      value: 'popular',
    },
    {
      label: 'Select manually',
      value: 'manual',
    },
  ]);

  const [tabDisplay, setTabDisplay] = useState<CommonHash>({
    label:
      tabDisplayOptions.find(
        (option) =>
          option.value === landing_component.sub_landing_components[index]['config']['tab_display']
      )?.label || 'Select popular ones',
    value: landing_component.sub_landing_components[index]['config']['tab_display'] || 'popular',
  });
  const loadInitialSelectedCards = () => {
    if (tabType.value === 'subjects' && landing_component.sub_landing_components[index]?.subjects) {
      return landing_component.sub_landing_components[index].subjects.map((subject: Subject) => ({
        label: subject.name,
        value: subject.id,
      }));
    } else if (
      tabType.value === 'programs' &&
      landing_component.sub_landing_components[index]?.programs
    ) {
      return landing_component.sub_landing_components[index].programs.map((program: Program) => ({
        label: program.name,
        value: program.id,
      }));
    } else if (
      tabType.value === 'schools' &&
      landing_component.sub_landing_components[index]?.schools
    ) {
      return landing_component.sub_landing_components[index].schools.map((school: School) => ({
        label: school.name,
        value: school.id,
      }));
    } else if (
      tabType.value === 'cities' &&
      landing_component.sub_landing_components[index]?.cities
    ) {
      return landing_component.sub_landing_components[index].cities.map((city: City) => ({
        label: city.name,
        value: city.id,
      }));
    } else if (
      tabType.value === 'countries' &&
      landing_component.sub_landing_components[index]?.countries
    ) {
      return landing_component.sub_landing_components[index].countries.map((country: Country) => ({
        label: country.name,
        value: country.id,
      }));
    }

    return [];
  };
  const [selectedCards, setSelectedCards] = useState<CommonHash[]>(loadInitialSelectedCards());
  const [locationFilters, setLocationFilters] = useState<CommonHash[]>([]);
  const [subjectFilters, setSubjectFilters] = useState<CommonHash[]>([]);
  const [durationFilter, setDurationFilter] = useState<CommonHash>({});
  const [tuitionFilter, setTuitionFilter] = useState<CommonHash>({});
  const [schoolFilter, setSchoolFilter] = useState<CommonHash>({});

  const typeFilterButtonOptions = [
    { label: 'None', value: 'none' },
    { label: 'Degree', value: 'degree' },
    { label: 'Country', value: 'country' },
    { label: 'Subject', value: 'subject' },
  ];

  const [typeFilterButton, setTypeFilterButton] = useState<CommonHash>({
    label:
      typeFilterButtonOptions.find(
        (option) =>
          option.value ===
          landing_component.sub_landing_components[index]['config']['button_filter_type']
      )?.label || 'None',
    value:
      landing_component.sub_landing_components[index]['config']['button_filter_type'] || 'none',
  });
  const handleInitialButtonFiltersSelected = () => {
    if (landing_component.sub_landing_components[index].filters) {
      return landing_component.sub_landing_components[index].filters.map((filter: CommonHash) => ({
        label: filter.name,
        value: filter.id,
      }));
    }
    return [];
  };

  const [buttonFiltersSelected, setButtonFiltersSelected] = useState<CommonHash[]>(
    handleInitialButtonFiltersSelected()
  );
  const { data: subjects, isLoading: subjectsLoading } = useGetAllSubjectsQuery();
  const { data: countries, isLoading: countriesLoading } = useGetAllCountriesQuery();
  const { data: cities, isLoading: citiesLoading } = useGetCitiesSearchQuery();
  const { data: programs, isLoading: programsLoading } = useGetAllBackofficeProgramsQuery({});
  const { data: schools, isLoading: schoolsLoading } = useGetSchoolsSearchQuery();

  const durationFilterOptions = [
    { label: '12 months', value: 12 },
    { label: '18 months', value: 18 },
    { label: '24 months', value: 24 },
  ];

  const tuitionFilterOptions = [
    { label: '<5K', value: '0-5' },
    { label: '5-10K', value: '5-10' },
    { label: '10-20K', value: '10-20' },
    { label: '20-30K', value: '20-30' },
    { label: '30-50K', value: '30-50' },
    { label: '50K+', value: '50' },
  ];

  const changeTabType = (tabType: CommonHash) => {
    handleChange({
      target: {
        name: `${name}[${index}].config['tab_type']`,
        value: tabType.value,
      },
    });
    if (tabType.value === 'programs' || tabType.value === 'schools') {
      setTabDisplayOptions([
        {
          label: 'Show popular ones',
          value: 'popular',
        },
        {
          label: 'Choose from filtered search',
          value: 'filtered',
        },
        {
          label: 'Select manually',
          value: 'manual',
        },
      ]);
    } else {
      setTabDisplayOptions([
        {
          label: 'Show popular ones',
          value: 'popular',
        },
        {
          label: 'Select manually',
          value: 'manual',
        },
      ]);
    }
    setSelectedCards([]);
    setLocationFilters([]);
    setSubjectFilters([]);
    setDurationFilter({});
    setTuitionFilter({});
    setSchoolFilter({});
    setTypeFilterButton({
      label: 'None',
      value: 'none',
    });
    setButtonFiltersSelected([]);

    setTabDisplay({
      label: 'Show popular ones',
      value: 'popular',
    });
  };

  useEffect(() => {
    handleChange({
      target: {
        name: `${name}[${index}].config['tab_display']`,
        value: tabDisplay.value,
      },
    });
  }, [tabDisplay]);

  useEffect(() => {
    handleChange({
      target: {
        name: `${name}[${index}].config['tab_selected_cards']`,
        value: selectedCards.map((card) => card.value),
      },
    });
  }, [selectedCards]);

  useEffect(() => {
    handleChange({
      target: {
        name: `${name}[${index}].config['location_filters']`,
        value: locationFilters.map((filter) => filter.value),
      },
    });
  }, [locationFilters]);

  useEffect(() => {
    handleChange({
      target: {
        name: `${name}[${index}].config['subject_filters']`,
        value: subjectFilters.map((filter) => filter.value),
      },
    });
  }, [subjectFilters]);

  useEffect(() => {
    if (durationFilter) {
      handleChange({
        target: {
          name: `${name}[${index}].config['duration_filter']`,
          value: durationFilter.value,
        },
      });
    }
  }, [durationFilter]);

  useEffect(() => {
    if (tuitionFilter) {
      handleChange({
        target: {
          name: `${name}[${index}].config['tuition_filter']`,
          value: tuitionFilter.value,
        },
      });
    }
  }, [tuitionFilter]);
  useEffect(() => {
    if (schoolFilter) {
      handleChange({
        target: {
          name: `${name}[${index}].config['school_filter']`,
          value: schoolFilter.value,
        },
      });
    }
  }, [schoolFilter]);

  const changeTypeFilterButton = (typeFilterButton: CommonHash) => {
    setTypeFilterButton(typeFilterButton);
    handleChange({
      target: {
        name: `${name}[${index}].config['button_filter_type']`,
        value: typeFilterButton.value,
      },
    });
    setButtonFiltersSelected([]);
  };

  useEffect(() => {
    handleChange({
      target: {
        name: `${name}[${index}].config['button_filters_selected']`,
        value: buttonFiltersSelected.map((filter) => filter.value),
      },
    });
  }, [buttonFiltersSelected]);
  return (
    <>
      <Text size={22}> Tab {index + 1} </Text>
      <div className="d-flex flex-column gap-3 mt-2">
        <div className={styles.group}>
          <label htmlFor={`${name}[${index}].config['tab_name']`}>Tab name</label>
          <input
            name={`${name}[${index}].config['tab_name']`}
            placeholder="Tab name"
            onChange={handleChange}
            required={true}
            value={landing_component.sub_landing_components[index]['config']['tab_name'] || ''}
          />
        </div>
        <div className={styles.group}>
          <label htmlFor={`${name}[${index}].config['tab_type']`}>Type of content</label>
          <div className={styles.selectorAttribute}>
            <Select
              isSearchable
              classNamePrefix="react-select"
              placeholder="Select Content Type"
              options={tabTypesOptions}
              onChange={(tabType: any) => {
                setTabType(tabType);
                changeTabType(tabType);
              }}
              value={tabType}
            />
          </div>
        </div>
        {(tabType.value === 'programs' || tabType.value === 'schools') && (
          <div className={styles.group}>
            <label htmlFor={`${name}[${index}].config['tab_style']`}>Tab Style</label>
            <select
              name={`${name}[${index}].config['tab_style']`}
              onChange={handleChange}
              value={
                landing_component.sub_landing_components[index]['config']['tab_style']
                  ? landing_component.sub_landing_components[index]['config']['tab_style']
                  : 'standard'
              }
            >
              <option value="standard">Standard</option>
              <option value="explore">Explore</option>
            </select>
          </div>
        )}
        <div className={styles.group}>
          <label htmlFor={`${name}[${index}].config['button_text']`}>Optional button text</label>
          <input
            name={`${name}[${index}].config['button_text']`}
            placeholder="Button text"
            onChange={handleChange}
            value={landing_component.sub_landing_components[index]['config']['button_text'] || ''}
          />
        </div>
        <div className={styles.group}>
          <label htmlFor={`${name}[${index}].config['button_link']`}>Optional button link</label>
          <input
            name={`${name}[${index}].config['button_link']`}
            placeholder="Button link"
            onChange={handleChange}
            value={landing_component.sub_landing_components[index]['config']['button_link'] || ''}
          />
        </div>
        <div className={styles.group}>
          <label htmlFor={`${name}[${index}].style`}>Optional button style</label>
          <select
            name={`${name}[${index}].config['button_style']`}
            onChange={handleChange}
            value={
              landing_component.sub_landing_components[index]['config']['button_style']
                ? landing_component.sub_landing_components[index]['config']['button_style']
                : 'primary'
            }
          >
            <option value="primary">Primary</option>
            <option value="secondary">Secondary</option>
            <option value="light">Light</option>
          </select>
        </div>
        {tabType.value !== 'blogs' && (
          <div className={styles.group}>
            <label htmlFor={`${name}[${index}].config['tab_display']`}>Tab Display</label>
            <div className={styles.selectorAttribute}>
              <Select
                isSearchable
                classNamePrefix="react-select"
                placeholder="Select Tab Display"
                options={tabDisplayOptions}
                onChange={(tabDisplay: any) => {
                  setTabDisplay(tabDisplay);
                }}
                value={tabDisplay}
              />
            </div>
          </div>
        )}

        {tabDisplay.value === 'popular' && (
          <div className={styles.group}>
            <label htmlFor={`${name}[${index}].config['tab_cards_amount']`}>
              How many you want to show
            </label>
            <input
              name={`${name}[${index}].config['tab_cards_amount']`}
              type="number"
              placeholder="Populars amount"
              onChange={handleChange}
              required={true}
              value={
                landing_component.sub_landing_components[index]['config']['tab_cards_amount'] || ''
              }
            />
          </div>
        )}
        {tabDisplay.value === 'manual' && (
          <div className={styles.group}>
            <label htmlFor={`${name}[${index}].config['tab_selected_cards']`}>
              Select the ones to show
            </label>
            <div className={styles.selectorAttribute}>
              {tabType.value === 'subjects' && (
                <Select
                  isClearable
                  isSearchable
                  isMulti
                  hideSelectedOptions={false}
                  classNamePrefix="react-select"
                  placeholder="Search for subjects to show"
                  options={subjects?.data.map((c) => ({ label: c.name, value: c.id })) || []}
                  onChange={(selectedOptions: any) =>
                    setSelectedCards(selectedOptions.map((option: CommonHash) => option))
                  }
                  isLoading={subjectsLoading}
                  value={selectedCards}
                />
              )}
              {tabType.value === 'countries' && (
                <Select
                  isClearable
                  isSearchable
                  isMulti
                  hideSelectedOptions={false}
                  classNamePrefix="react-select"
                  placeholder="Search for countries to show"
                  options={countries?.data.map((c) => ({ label: c.name, value: c.id })) || []}
                  onChange={(selectedOptions: any) =>
                    setSelectedCards(selectedOptions.map((option: CommonHash) => option))
                  }
                  isLoading={countriesLoading}
                  value={selectedCards}
                />
              )}
              {tabType.value === 'cities' && (
                <Select
                  isClearable
                  isSearchable
                  isMulti
                  hideSelectedOptions={false}
                  classNamePrefix="react-select"
                  placeholder="Search for cities to show"
                  options={cities?.data.map((c) => ({ label: c.name, value: c.id })) || []}
                  onChange={(selectedOptions: any) =>
                    setSelectedCards(selectedOptions.map((option: CommonHash) => option))
                  }
                  isLoading={citiesLoading}
                  value={selectedCards}
                />
              )}
              {tabType.value === 'programs' && (
                <Select
                  isClearable
                  isSearchable
                  isMulti
                  hideSelectedOptions={false}
                  classNamePrefix="react-select"
                  placeholder="Search for programs to show"
                  options={programs?.data.map((c) => ({ label: c.name, value: c.id })) || []}
                  onChange={(selectedOptions: any) =>
                    setSelectedCards(selectedOptions.map((option: CommonHash) => option))
                  }
                  isLoading={programsLoading}
                  value={selectedCards}
                />
              )}
              {tabType.value === 'schools' && (
                <Select
                  isClearable
                  isSearchable
                  isMulti
                  hideSelectedOptions={false}
                  classNamePrefix="react-select"
                  placeholder="Search for schools to show"
                  options={schools?.data.map((c) => ({ label: c.name, value: c.id })) || []}
                  onChange={(selectedOptions: any) =>
                    setSelectedCards(selectedOptions.map((option: CommonHash) => option))
                  }
                  isLoading={schoolsLoading}
                  value={selectedCards}
                />
              )}
            </div>
          </div>
        )}
        {tabDisplay.value === 'filtered' && (
          <>
            <div className={styles.group}>
              <label htmlFor={`${name}[${index}].config['tab_cards_amount']`}>
                How many filtered ones you want to show
              </label>
              <input
                name={`${name}[${index}].config['tab_cards_amount']`}
                type="number"
                placeholder="Filtered amount"
                onChange={handleChange}
                required={true}
                value={
                  landing_component.sub_landing_components[index]['config']['tab_cards_amount'] ||
                  ''
                }
              />
            </div>
            <div className={styles.group}>
              <label htmlFor={`${name}[${index}].config['show_hidden']`}>Display hidden ones</label>
              <Field
                name={`${name}[${index}].config['show_hidden']`}
                type="checkbox"
                defaultChecked={
                  landing_component.sub_landing_components[index]['config']['show_hidden']
                }
              />
            </div>
            <div className={styles.group}>
              <label htmlFor={`${name}[${index}].config['only_partner']`}>
                Only partner schools
              </label>
              <Field
                name={`${name}[${index}].config['only_partner']`}
                type="checkbox"
                value={landing_component.sub_landing_components[index]['config']['only_partner']}
              />
            </div>
            <div className={styles.group}>
              <label htmlFor={`${name}[${index}].config['location_filters']`}>
                Select the location filters
              </label>
              <div className={styles.selectorAttribute}>
                <Select
                  isClearable
                  isSearchable
                  isMulti
                  hideSelectedOptions={false}
                  classNamePrefix="react-select"
                  placeholder="Search for locations to filter"
                  options={countries?.data.map((c) => ({ label: c.name, value: c.id })) || []}
                  onChange={(selectedOptions: any) =>
                    setLocationFilters(selectedOptions.map((option: CommonHash) => option))
                  }
                  isLoading={countriesLoading}
                  value={locationFilters}
                />
              </div>
            </div>
            <div className={styles.group}>
              <label htmlFor={`${name}[${index}].config['subject_filters']`}>
                Select the subject filters
              </label>
              <div className={styles.selectorAttribute}>
                <Select
                  isClearable
                  isSearchable
                  isMulti
                  hideSelectedOptions={false}
                  classNamePrefix="react-select"
                  placeholder="Search for subjects to filter"
                  options={subjects?.data.map((c) => ({ label: c.name, value: c.id })) || []}
                  onChange={(selectedOptions: any) =>
                    setSubjectFilters(selectedOptions.map((option: CommonHash) => option))
                  }
                  isLoading={subjectsLoading}
                  value={subjectFilters}
                />
              </div>
            </div>
            <div className={styles.group}>
              <label htmlFor={`${name}[${index}].config['duration_filter']`}>
                Select the duration filter
              </label>
              <div className={styles.selectorAttribute}>
                <Select
                  isClearable
                  isSearchable
                  hideSelectedOptions={false}
                  classNamePrefix="react-select"
                  placeholder="Search for durations to filter"
                  options={durationFilterOptions}
                  onChange={(selectedOption: any) => setDurationFilter(selectedOption)}
                  value={durationFilter}
                />
              </div>
            </div>
            <div className={styles.group}>
              <label htmlFor={`${name}[${index}].config['tuition_filter']`}>
                Select the tuition filter
              </label>
              <div className={styles.selectorAttribute}>
                <Select
                  isClearable
                  isSearchable
                  hideSelectedOptions={false}
                  classNamePrefix="react-select"
                  placeholder="Search for tuition to filter"
                  options={tuitionFilterOptions}
                  onChange={(selectedOption: any) => setTuitionFilter(selectedOption)}
                  value={tuitionFilter}
                />
              </div>
            </div>
            {tabType.value === 'programs' && (
              <div className={styles.group}>
                <label htmlFor={`${name}[${index}].config['school_filter']`}>
                  Select the school filter
                </label>
                <div className={styles.selectorAttribute}>
                  <Select
                    isClearable
                    isSearchable
                    hideSelectedOptions={false}
                    classNamePrefix="react-select"
                    placeholder="Search for school to filter"
                    options={schools?.data.map((c) => ({ label: c.name, value: c.id })) || []}
                    onChange={(selectedOption: any) => setSchoolFilter(selectedOption)}
                    value={schoolFilter}
                  />
                </div>
              </div>
            )}
          </>
        )}
        {tabType.value === 'programs' && (
          <>
            <div className={styles.group}>
              <label htmlFor={`${name}[${index}].config['button_filter_type']`}>
                Add button filters on Frontend for programs. Choose type of filter
              </label>
              <div className={styles.selectorAttribute}>
                <Select
                  classNamePrefix="react-select"
                  placeholder="Search for filters to apply"
                  options={typeFilterButtonOptions}
                  onChange={(selectedOption: any) => changeTypeFilterButton(selectedOption)}
                  value={typeFilterButton}
                />
              </div>
            </div>
            {typeFilterButton.value === 'country' && (
              <div className={styles.group}>
                <label htmlFor={`${name}[${index}].config['button_filters_selected']`}>
                  Select the countries to allow the user to filter programs on frontend.
                </label>
                <div className={styles.selectorAttribute}>
                  <Select
                    isClearable
                    isSearchable
                    isMulti
                    hideSelectedOptions={false}
                    isOptionDisabled={() => buttonFiltersSelected.length >= 4}
                    classNamePrefix="react-select"
                    placeholder="Search for locations to filter on frontend"
                    options={countries?.data.map((c) => ({ label: c.name, value: c.id })) || []}
                    onChange={(selectedOptions: any) =>
                      setButtonFiltersSelected(selectedOptions.map((option: CommonHash) => option))
                    }
                    isLoading={countriesLoading}
                    value={buttonFiltersSelected}
                  />
                </div>
              </div>
            )}
            {typeFilterButton.value === 'subject' && (
              <div className={styles.group}>
                <label htmlFor={`${name}[${index}].config['button_filters_selected']`}>
                  Select the subjects to allow the user to filter programs on frontend.
                </label>
                <div className={styles.selectorAttribute}>
                  <Select
                    isClearable
                    isSearchable
                    isMulti
                    hideSelectedOptions={false}
                    isOptionDisabled={() => buttonFiltersSelected.length >= 4}
                    classNamePrefix="react-select"
                    placeholder="Search for subjects to filter on frontend"
                    options={subjects?.data.map((c) => ({ label: c.name, value: c.id })) || []}
                    onChange={(selectedOptions: any) =>
                      setButtonFiltersSelected(selectedOptions.map((option: CommonHash) => option))
                    }
                    isLoading={subjectsLoading}
                    value={buttonFiltersSelected}
                  />
                </div>
              </div>
            )}
            {typeFilterButton.value === 'duration' && (
              <div className={styles.group}>
                <label htmlFor={`${name}[${index}].config['button_filters_selected']`}>
                  Select the durations to allow the user to filter programs on frontend.
                </label>
                <div className={styles.selectorAttribute}>
                  <Select
                    isClearable
                    isSearchable
                    isMulti
                    hideSelectedOptions={false}
                    isOptionDisabled={() => buttonFiltersSelected.length >= 4}
                    classNamePrefix="react-select"
                    placeholder="Choose the durations to filter on frontend"
                    options={durationFilterOptions}
                    onChange={(selectedOptions: any) =>
                      setButtonFiltersSelected(selectedOptions.map((option: CommonHash) => option))
                    }
                    value={buttonFiltersSelected}
                  />
                </div>
              </div>
            )}
            {typeFilterButton.value === 'tuition' && (
              <div className={styles.group}>
                <label htmlFor={`${name}[${index}].config['button_filters_selected']`}>
                  Select the tuitions to allow the user to filter programs on frontend.
                </label>
                <div className={styles.selectorAttribute}>
                  <Select
                    isClearable
                    isSearchable
                    isMulti
                    hideSelectedOptions={false}
                    isOptionDisabled={() => buttonFiltersSelected.length >= 4}
                    classNamePrefix="react-select"
                    placeholder="Select the tuition ranges to filter on frontend"
                    options={tuitionFilterOptions}
                    onChange={(selectedOptions: any) =>
                      setButtonFiltersSelected(selectedOptions.map((option: CommonHash) => option))
                    }
                    value={buttonFiltersSelected}
                  />
                </div>
              </div>
            )}
          </>
        )}
        <Button
          type="button"
          variant="danger"
          className={styles.deleteButton}
          onClick={() => {
            arrayHelpers.remove(index);
          }}
        >
          Delete
        </Button>
      </div>
      {index === landing_component.sub_landing_components.length - 1 &&
        landing_component.sub_landing_components.length <= limit - 1 && (
          <Button
            type="button"
            className="mt-3"
            variant="success"
            onClick={() =>
              arrayHelpers.push({
                config: {
                  tab_name: '',
                  tab_type: 'subjects',
                  tab_style: 'standard',
                  button_style: 'primary',
                },
              })
            }
          >
            + Tab
          </Button>
        )}
    </>
  );
};

export default TabForm;
